import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { useCallback, useEffect, useRef, useState } from "react";

import { DIALOG_BACKDROP_SX, dialogPaperSurface } from "@/components/Core/Dialogs/surface";
import { HEALTH_CHECK_QUERY_KEY } from "@/components/Core/HealthGate/data";
import { routers } from "@/configs";
import { useIsLoggedIn, useTranslate } from "@/hooks";
import { queryClient } from "@/lib/queryClient";
import routes from "@/routes";
import { RG_CHANGED_EVENT, getRgSessionSummary, logoutUser } from "@/services";
import { dispatch, logout, persister } from "@/store";
import { RgSessionSummary } from "@/types";
import { formatBalance } from "@/utils";

const MINUTE = 60_000;

/** Milliseconds until the next whole multiple of `everyMinutes` since the session started. */
const untilNext = (summary: RgSessionSummary, everyMinutes: number) => {
  const elapsed = Date.now() - new Date(summary.startedAt).getTime();
  const every = everyMinutes * MINUTE;
  return Math.max(1000, (Math.floor(elapsed / every) + 1) * every - elapsed);
};

/**
 * The reality check: at the interval the player chose, a dialog with the time played since
 * login and this session's real-money result. When the session time limit is reached it
 * offers only signing out. Renders nothing for a guest or a player without these settings.
 */
export default function RealityCheck() {
  const t = useTranslate();
  const theme = useTheme();
  const isLoggedIn = useIsLoggedIn();
  const [summary, setSummary] = useState<RgSessionSummary | null>(null);
  const [open, setOpen] = useState<"check" | "limit" | null>(null);
  const timers = useRef<number[]>([]);

  const clearTimers = () => {
    timers.current.forEach((id) => window.clearTimeout(id));
    timers.current = [];
  };

  const refresh = useCallback(async () => {
    try {
      const next = await getRgSessionSummary();
      setSummary(next);
      return next;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (!isLoggedIn) {
      clearTimers();
      setSummary(null);
      setOpen(null);
      return;
    }
    void refresh();
    const onChanged = () => void refresh();
    window.addEventListener(RG_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(RG_CHANGED_EVENT, onChanged);
  }, [isLoggedIn, refresh]);

  // re-arm from the latest summary: the next reality check and the session limit
  useEffect(() => {
    clearTimers();
    if (!summary || open) return;
    if (summary.sessionLimitReached) {
      setOpen("limit");
      return;
    }
    if (summary.realityCheckMinutes) {
      timers.current.push(
        window.setTimeout(
          async () => {
            if (await refresh()) setOpen("check");
          },
          untilNext(summary, summary.realityCheckMinutes),
        ),
      );
    }
    if (summary.sessionLimitMinutes) {
      const left =
        new Date(summary.startedAt).getTime() + summary.sessionLimitMinutes * MINUTE - Date.now();
      timers.current.push(
        window.setTimeout(
          async () => {
            const next = await refresh();
            if (next?.sessionLimitReached) setOpen("limit");
          },
          Math.max(1000, left),
        ),
      );
    }
    return clearTimers;
  }, [summary, open, refresh]);

  const signOut = async () => {
    setOpen(null);
    // End the session on the server too; the browser is cleared either way.
    await logoutUser().catch(() => undefined);
    dispatch(logout());
    queryClient.removeQueries({
      predicate: (query) => query.queryKey[0] !== HEALTH_CHECK_QUERY_KEY[0],
    });
    void persister.flush();
    void routes.navigate(routers.Home);
  };

  if (!isLoggedIn || !summary || !open) return null;

  const hours = Math.floor(summary.elapsedMinutes / 60);
  const minutes = summary.elapsedMinutes % 60;
  const played = t("reality-check.time", { hours, minutes });
  const money = (value: number) => formatBalance(value, summary.currency);

  return (
    <Dialog
      open
      maxWidth="xs"
      fullWidth
      // the player answers it: a click outside does not count as "continue"
      onClose={undefined}
      PaperProps={{ sx: dialogPaperSurface(theme) }}
      slotProps={{ backdrop: { sx: DIALOG_BACKDROP_SX } }}
    >
      <DialogTitle sx={{ fontWeight: 800 }}>
        {t(open === "limit" ? "reality-check.limit-title" : "reality-check.title")}
      </DialogTitle>
      <DialogContent>
        <Typography>
          {open === "limit"
            ? t("reality-check.limit-desc", {
                time: t("reality-check.time", {
                  hours: Math.floor((summary.sessionLimitMinutes ?? 0) / 60),
                  minutes: (summary.sessionLimitMinutes ?? 0) % 60,
                }),
              })
            : t("reality-check.played", { time: played })}
        </Typography>
        <Stack gap={0.5} sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t("reality-check.wagered")}: {money(summary.wagered)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("reality-check.won")}: {money(summary.won)}
          </Typography>
          <Typography fontWeight={800} color={summary.net < 0 ? "error.main" : "success.main"}>
            {t("reality-check.result")}: {money(summary.net)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t("reality-check.real-money-note")}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button color="inherit" onClick={signOut} sx={{ borderRadius: 999 }}>
          {t("reality-check.stop")}
        </Button>
        {open === "check" && (
          <Button
            variant="contained"
            onClick={() => setOpen(null)}
            sx={{ borderRadius: 999, minWidth: 120 }}
          >
            {t("reality-check.continue")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
